/**
 * Analytics Portal V1 — result impression tracker (Phase 4, amendment A4-1).
 *
 * An impression counts only when a real result is at least 50% visible for at least
 * 300 ms in a VISIBLE tab, once per server-signed token. Never on re-render, never on
 * scroll-back (the element is unobserved after it counts), never in a hidden tab, and
 * never at all when the browser has no `IntersectionObserver`.
 */
import { useEffect, useMemo } from "react";
import { trackResult } from "@/lib/analytics";

export const IMPRESSION_RATIO = 0.5;
export const IMPRESSION_DWELL_MS = 300;
const MAX_TRACKED = 800;

function createTracker(eventName) {
  if (typeof IntersectionObserver === "undefined" || typeof document === "undefined") {
    return { bind: () => undefined, destroy: () => {} };
  }
  const payloads = new Map();   // node -> analytics payload
  const timers = new Map();     // node -> dwell timeout
  const binds = new Map();      // token -> stable ref callback
  const sent = new Set();       // tokens already counted

  const clearDwell = (node) => {
    const t = timers.get(node);
    if (t) { clearTimeout(t); timers.delete(node); }
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const payload = payloads.get(entry.target);
      if (!payload) return;
      const visible = entry.isIntersecting && entry.intersectionRatio >= IMPRESSION_RATIO
        && document.visibilityState === "visible";
      if (!visible) { clearDwell(entry.target); return; }
      if (timers.has(entry.target) || sent.has(payload.token)) return;
      timers.set(entry.target, setTimeout(() => {
        timers.delete(entry.target);
        if (sent.has(payload.token) || document.visibilityState !== "visible") return;
        if (sent.size > MAX_TRACKED) sent.clear();
        sent.add(payload.token);
        observer.unobserve(entry.target);
        trackResult(eventName, payload);
      }, IMPRESSION_DWELL_MS));
    });
  }, { threshold: [0, IMPRESSION_RATIO] });

  const onVisibility = () => {
    if (document.visibilityState !== "visible") {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    }
  };
  document.addEventListener("visibilitychange", onVisibility);

  const detach = (node) => {
    clearDwell(node);
    payloads.delete(node);
    try { observer.unobserve(node); } catch (_) { /* already gone */ }
  };

  // One stable ref callback per token so a re-render never re-attaches or re-times.
  const bind = (analytics) => {
    if (!analytics || !analytics.token) return undefined;
    const token = analytics.token;
    let cb = binds.get(token);
    if (!cb) {
      cb = (node) => {
        if (!node || sent.has(token)) return undefined;
        payloads.set(node, analytics);
        observer.observe(node);
        return () => detach(node);
      };
      if (binds.size > MAX_TRACKED) binds.clear();
      binds.set(token, cb);
    }
    return cb;
  };

  const destroy = () => {
    timers.forEach((t) => clearTimeout(t));
    timers.clear();
    payloads.clear();
    binds.clear();
    observer.disconnect();
    document.removeEventListener("visibilitychange", onVisibility);
  };

  return { bind, destroy };
}

export function useAnalyticsImpressions(eventName) {
  const tracker = useMemo(() => createTracker(eventName), [eventName]);
  useEffect(() => () => tracker.destroy(), [tracker]);
  return tracker.bind;
}

export { createTracker as createImpressionTracker };
